import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useCamp } from "@/context/SettingsContext";
import { STATUS } from "@/lib/constants";

function PctField({ color, bg, value, onChange, onEnter, testid }) {
  return (
    <div className="flex items-center gap-1 rounded-lg px-1.5 py-0.5" style={{ backgroundColor: bg }}>
      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <input type="number" min={0} max={100} value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onEnter(); } }}
        className="h-6 w-9 border-0 bg-transparent p-0 text-right text-xs font-bold tabular-nums outline-none focus:ring-0"
        style={{ color }} data-testid={testid} />
      <span className="text-xs font-bold" style={{ color }}>%</span>
    </div>
  );
}

export default function DemoFillSwitch({ onReload }) {
  const { t } = useLang();
  const { camp } = useCamp();
  const [occ, setOcc] = useState("0");
  const [res, setRes] = useState("0");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!camp?.demo_mode) return;
    api.get("/dummy/fill")
      .then(({ data }) => { setOcc(String(data.occupied_pct ?? 0)); setRes(String(data.reserved_pct ?? 0)); })
      .catch(() => {});
  }, [camp?.demo_mode]);

  if (!camp?.demo_mode) return null;

  const clamp = (v) => Math.max(0, Math.min(100, parseInt(v || "0", 10) || 0));

  const apply = async () => {
    const occupied = clamp(occ), reserved = clamp(res);
    if (occupied + reserved > 100) { toast.error(t("demo_fill_sum_err")); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/dummy/fill?occupied_pct=${occupied}&reserved_pct=${reserved}`);
      setOcc(String(occupied)); setRes(String(reserved));
      toast.success(t("demo_fill_done").replace("{b}", data.added_occupied).replace("{r}", data.added_reserved));
      onReload && onReload();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-1 rounded-xl border border-border bg-card p-1 shadow-sm"
      title={t("demo_fill_hint")} data-testid="demo-fill-control">
      <PctField color={STATUS.bezet.color} bg={STATUS.bezet.bg} value={occ}
        onChange={setOcc} onEnter={apply} testid="demo-fill-occupied" />
      <PctField color={STATUS.gereserveerd.color} bg={STATUS.gereserveerd.bg} value={res}
        onChange={setRes} onEnter={apply} testid="demo-fill-reserved" />
      <button type="button" onClick={apply} disabled={busy} title={t("demo_fill_apply")}
        data-testid="demo-fill-apply"
        className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-600 text-white transition-colors hover:bg-emerald-700 disabled:opacity-50">
        <Check className="h-4 w-4" />
      </button>
    </div>
  );
}
